/**
 * DetailModalBody Component
 *
 * Main content component for the FlowReport detail modal.
 * Displays client details, action logs, and user action forms.
 */

import React, { useMemo } from "react";
import {
  DetailModalBodyProps,
  FlowUserAction,
  ActionLogEntry,
} from "./FlowReport.utils";
import ClientDetailsSection from "./ClientDetailSection";
import ActionLogSection from "./ActionLogSection";
import UserActionSection from "./UserActionSection";
import styles from "./FlowReport.module.css";

/**
 * DetailModalBody Component
 */
const DetailModalBody: React.FC<DetailModalBodyProps> = ({
  row,
  flowsConfig,
  reportMode,
  beautifiedNames = {},
  onUserActionUpdate,
  onRefresh,
}) => {
  /**
   * Build user action flows array
   */
  const userActionFlows = useMemo((): FlowUserAction[] => {
    const flows: FlowUserAction[] = [];

    if (reportMode === "multi" && flowsConfig.length > 0) {
      flowsConfig.forEach((flowConfig) => {
        const alias = flowConfig.alias;
        flows.push({
          flowAlias: alias,
          flowDisplayName: flowConfig.displayName,
          reportId: row[`${alias}_reportId`] || null,
          status: row[`${alias}_userActionStatus`] || "",
          comment: row[`${alias}_userComment`] || "",
        });
      });
    } else {
      // Single-flow: One record
      flows.push({
        flowAlias: "default",
        flowDisplayName: "Flow Record",
        reportId: row.reportId || row.FlowOrchestratorReportId || null,
        status: row.userActionStatus || row.UserActionStatus || "",
        comment: row.userComment || row.UserComment || "",
      });
    }

    return flows;
  }, [row, flowsConfig, reportMode]);

  /**
   * Build flow action logs array
   * Fixed: Properly extract Message field with multiple casing options
   */
  const actionLogs = useMemo((): ActionLogEntry[] => {
    const logs: ActionLogEntry[] = [];

    const normalizeTimestamp = (value: any): string | number | undefined => {
      if (value === null || value === undefined) return undefined;
      return value;
    };

    const parseRawReportLog = (
      reportLog: any,
      flowAlias: string,
      flowDisplayName: string,
    ) => {
      if (!reportLog) return;

      try {
        const parsed =
          typeof reportLog === "string" ? JSON.parse(reportLog) : reportLog;

        if (!Array.isArray(parsed)) return;

        parsed.forEach((entry: any) => {
          // Extract message with multiple casing fallbacks
          const message =
            entry.Message ||
            entry.message ||
            entry.MSG ||
            entry.msg ||
            entry.Description ||
            entry.description ||
            "";

          logs.push({
            flowAlias,
            flowDisplayName,
            action: entry.Action || entry.action || "",
            status: entry.Status || entry.status || "",
            message: message,
            timestamp: normalizeTimestamp(entry.Timestamp || entry.timestamp),
          });
        });
      } catch (error) {
        console.error(`Failed to parse reportLog for ${flowAlias}:`, error);
      }
    };

    if (reportMode === "multi" && flowsConfig.length > 0) {
      // Multi-flow: Parse each flow's reportLog
      flowsConfig.forEach((flowConfig) => {
        const alias = flowConfig.alias;
        const displayName = flowConfig.displayName;
        const reportLog = row[`${alias}_reportLog`];

        if (reportLog) {
          parseRawReportLog(reportLog, alias, displayName);
        }
      });
    } else {
      // Single-flow: Parse the single reportLog
      const reportLog = row.reportLog || row.ReportLog;

      if (reportLog) {
        // For single flow, we don't need flow alias display
        parseRawReportLog(reportLog, "single", "");
      }
    }

    return logs;
  }, [row, flowsConfig, reportMode]);

  /**
   * Handle user action update from UserActionSection
   */
  const handleUserActionUpdate = (
    flowAlias: string,
    reportId: number,
    status: string,
    comment: string,
  ) => {
    onUserActionUpdate?.(reportId, status, comment);
    onRefresh?.();
  };

  // In DetailModalBody.tsx, update the return section:

  return (
    <div className={styles.detailModalBody}>
      {/* Summary + User Action Row */}
      <div className={styles.summaryUserActionRow}>
        <ClientDetailsSection
          row={row}
          flowsConfig={flowsConfig}
          reportMode={reportMode}
        />
        <UserActionSection
          cygnusExecutionId={row.cygnusExecutionId || row._joinKey || ""}
          currentStatus={row.userActionStatus}
          currentComment={row.userComment}
          updatedBy={row.userActionUpdatedBy}
          updatedDate={row.userActionUpdatedDate}
          onSaveSuccess={(status, comment) => {
            console.log("Saved:", status, comment);
          }}
        />
      </div>

      {/* Action Log */}
      <ActionLogSection
        actionLogs={actionLogs}
        beautifiedNames={beautifiedNames}
        reportMode={reportMode}
      />
    </div>
  );
};

export default DetailModalBody;
